import { ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import Tooltip from '@/design_system/Tooltip';
import IconAdd from '@/icons/Add.svg';
import { createBEMClasses } from '@/utils/classname';

import './Avatar.css';

const { block } = createBEMClasses('avatar');

interface AvatarProps {
  variant?: 'greyedOut';
  url?: string | null;
  name?: string;
  icon?: ReactNode;
  count?: number;
  size?: 'xx-small' | 'x-small' | 'small' | 'medium' | 'large';
  isButton?: boolean;
  onPress?: () => void;
  ariaLabel?: string;
  tooltip?: React.ReactNode;
  disabled?: boolean;
}

const Avatar = ({
  variant,
  url,
  name,
  icon,
  count,
  size = 'large',
  isButton,
  onPress,
  ariaLabel,
  tooltip,
  disabled,
}: AvatarProps) => {
  const Base = ({ ...props }) =>
    tooltip ? (
      <Tooltip content={tooltip}>
        <AriaButton {...props} onPress={onPress} aria-label={ariaLabel} />
      </Tooltip>
    ) : isButton ? (
      <AriaButton {...props} onPress={onPress} aria-label={ariaLabel} />
    ) : (
      <div {...props} aria-label={ariaLabel} />
    );

  if (url) {
    return (
      <Base className={block({ size, image: true, variant, disabled })}>
        <img src={url} alt={name} />
      </Base>
    );
  }

  if (icon) {
    return <Base className={block({ size, icon: true, disabled })}>{icon}</Base>;
  }

  if (count) {
    return <Base className={block({ size, count: true, disabled })}>+{count}</Base>;
  }

  if (name) {
    // E.g: "John Fitzgerald (Support) Doe"
    const nameWithoutParentheses = name.replace(/\(.*?\)/g, '').trim(); // "John Fitzgerald  Doe"
    const parts = nameWithoutParentheses.split(' ').filter(Boolean); // ["John", "Fitzgerald", "Doe"]
    const firstAndLastParts = parts.length > 1 ? [parts[0], parts[parts.length - 1]] : [parts[0]]; // ["John", "Doe"]
    const initials = firstAndLastParts.map((part) => part.charAt(0).toUpperCase()).join(''); // "JD"

    return <Base className={block({ size, variant, disabled }, 'sentry-mask')}>{initials}</Base>;
  }

  return <Base className={block({ size, empty: true, disabled })}>{!disabled && <IconAdd />}</Base>;
};

export default Avatar;
